// ui/hero-timeline.js: Phase 3 #5a PURE timeline for the home-hero
// "gameplay demo" loop.
//
// This is NOT the real euchre engine. It is a fixed, scripted, decorative
// sequence that plays one canned hand on a loop behind the lobby header:
//
//     deal  →  order_up  →  trick  →  win  →  (loop)
//
// `heroFrameAt(tMs)` returns a plain, serializable description of the
// frame at time t. The canvas component (HeroAnimation.jsx) maps this
// frame onto the shared canvas renderers (table / trick-area / cards).
// All easing + interpolation lives here so it can be unit-tested without
// jsdom or a canvas mock.
//
// Loop contract: heroFrameAt(t) deep-equals heroFrameAt(t + LOOP_MS). The
// win phase fades the whole hand back out so the wrap to deal shows no jump.
//
// Geometry note: card x/y are UNIT FRACTIONS of the hero box (0..1), not
// pixels. The component multiplies by the hero canvas size. This keeps the
// timeline resolution-independent and trivially testable.

import { easeOut, springOvershoot, lerp } from './easing.js';

// ── Loop length + phase schedule ──────────────────────────────────────
// ~10s total, long enough to read the beat, short enough not to bore.
export const LOOP_MS = 10000;

// Phase start times (ms into the loop). Each phase runs until the next
// one's start; the last runs until LOOP_MS.
const PHASE_SCHEDULE = Object.freeze([
  { phase: 'deal',     start: 0 },
  { phase: 'order_up', start: 2500 },
  { phase: 'trick',    start: 4500 },
  { phase: 'win',      start: 8000 },
]);

export const PHASES = Object.freeze(['deal', 'order_up', 'trick', 'win']);

// ── Canned hand data ──────────────────────────────────────────────────
// Four seats, five cards each. Only the player's (seat 1) cards are shown
// face-up in the hero; the others read as a tasteful fan. Trump is Hearts,
// ordered up to the player so the order_up beat lands on "our" team.
const TRUMP_SUIT = 'H';

// The four cards that hit the center during the trick beat, keyed by the
// seat that plays them and the order they land. Seat 1 (player) wins with
// the right bower (JH), a satisfying canned outcome.
const TRICK_PLAYS = Object.freeze([
  { seat: 2, cardId: 'KC' },
  { seat: 3, cardId: 'QC' },
  { seat: 4, cardId: '9C' },
  { seat: 1, cardId: 'JH' }, // right bower for Hearts → wins
]);
const TRICK_WINNER_SEAT = 1;

// Resting fan position (unit fraction of the hero box) for each seat,
// matching the compass seating used by the real table: 1=bottom, 2=left,
// 3=top, 4=right. These are the "dealt" targets; cards fly in from the
// deck at center during the deal beat.
//
// Each card is offset INWARD (toward table center) off its own nameplate so
// the seat label stays fully visible, bottom card above its plate, top card
// below, side cards pulled in toward center. The plate edges (unit fraction)
// are 0.85 (bottom-plate top), 0.15 (top-plate bottom), 0.20 (left-plate
// right), 0.80 (right-plate left); with a ~0.105/0.042 half-card these rests
// keep a few px of clearance. See the no-overlap unit test.
const SEAT_REST = Object.freeze({
  1: { x: 0.50, y: 0.72 }, // bottom, above its nameplate
  2: { x: 0.27, y: 0.50 }, // left, pulled right (inward)
  3: { x: 0.50, y: 0.28 }, // top, below its nameplate
  4: { x: 0.73, y: 0.50 }, // right, pulled left (inward)
});

// Center of the hero box, the deck origin during the deal, and the trick
// landing zone during the trick beat.
const CENTER = Object.freeze({ x: 0.5, y: 0.5 });

// ── Shared hero-box geometry (set in one place only) ──────────────────
// The canvas component (HeroAnimation.jsx) draws at these logical px sizes
// and DERIVES its nameplate layout from them. Keeping them here lets the
// timeline expose nameplate + card rectangles as resolution-independent
// UNIT FRACTIONS, so the two can be checked for overlap in a pure unit test
// (no jsdom / canvas mock).
//
// Sized at 3× the original 360×200 footprint so the home hero reads as a
// proper "gameplay preview" rather than a thumbnail. Box, card, and plate
// all scale by the SAME factor, so every UNIT FRACTION below (and the
// no-overlap test that checks them) is unchanged, only the absolute px
// the canvas renders at grow. The CSS frame scales this down responsively
// on narrow viewports.
export const HERO_BOX = Object.freeze({ w: 1080, h: 600 });
// Decorative card draw size inside the hero box (px). The height mirrors the
// 2.5:3.5 playing-card ratio used by the component.
export const HERO_CARD = Object.freeze({ w: 90, h: Math.round(90 * 3.5 / 2.5) });
// Nameplate size + edge margin (px). The component builds its pixel layout
// from these so the plates here line up exactly with what's drawn.
export const HERO_PLATE = Object.freeze({ w: 192, h: 66, margin: 24 });

/**
 * Top-left pixel corner of seat `seat`'s nameplate inside the hero box.
 * Plates sit just inside each edge to echo the compass seating
 * (1=bottom, 2=left, 3=top, 4=right). The component consumes this so the
 * drawn plate and the rect tested here are one definition.
 *
 * @param {1|2|3|4} seat
 * @returns {{x:number,y:number}} pixel top-left
 */
export function seatNameplateXY(seat) {
  const { w: HW, h: HH } = HERO_BOX;
  const { w: pw, h: ph, margin: m } = HERO_PLATE;
  return {
    1: { x: (HW - pw) / 2, y: HH - ph - m }, // bottom
    2: { x: m,             y: (HH - ph) / 2 }, // left
    3: { x: (HW - pw) / 2, y: m },             // top
    4: { x: HW - pw - m,   y: (HH - ph) / 2 }, // right
  }[seat];
}

/**
 * Unit-fraction axis-aligned bounding box of seat `seat`'s nameplate.
 *
 * @param {1|2|3|4} seat
 * @returns {{x0:number,y0:number,x1:number,y1:number}} fractional AABB
 */
export function seatNameplateRect(seat) {
  const { w: HW, h: HH } = HERO_BOX;
  const { w: pw, h: ph } = HERO_PLATE;
  const { x, y } = seatNameplateXY(seat);
  return { x0: x / HW, y0: y / HH, x1: (x + pw) / HW, y1: (y + ph) / HH };
}

/**
 * Unit-fraction AABB of seat `seat`'s RESTING card (its dealt fan slot),
 * computed from SEAT_REST + the card draw size. The card is drawn centered
 * on its SEAT_REST point, so the rect is that point ± half the card size.
 *
 * @param {1|2|3|4} seat
 * @returns {{x0:number,y0:number,x1:number,y1:number}} fractional AABB
 */
export function seatCardRect(seat) {
  const rest = SEAT_REST[seat];
  const halfW = HERO_CARD.w / 2 / HERO_BOX.w;
  const halfH = HERO_CARD.h / 2 / HERO_BOX.h;
  return {
    x0: rest.x - halfW,
    y0: rest.y - halfH,
    x1: rest.x + halfW,
    y1: rest.y + halfH,
  };
}

// One card per seat is represented in the fan (kept light, this is
// decorative). The deal staggers the four seats so it reads as dealing.
const DEAL_ORDER = [1, 2, 3, 4];
const DEAL_STAGGER_FRAC = 0.18; // each seat starts 18% of the deal window later

// ── helpers ───────────────────────────────────────────────────────────

/** Round to 4 decimals so loop equality is exact (no float drift). */
function q(n) {
  return Math.round(n * 1e4) / 1e4;
}

/** Which phase is active at time t (t already wrapped into [0, LOOP_MS)). */
function phaseAt(t) {
  let current = PHASE_SCHEDULE[0];
  for (const p of PHASE_SCHEDULE) {
    if (t >= p.start) current = p;
    else break;
  }
  return current;
}

/** Start/end ms of a phase by name. */
function windowOf(phaseName) {
  const idx = PHASE_SCHEDULE.findIndex((p) => p.phase === phaseName);
  const start = PHASE_SCHEDULE[idx].start;
  const end = idx + 1 < PHASE_SCHEDULE.length ? PHASE_SCHEDULE[idx + 1].start : LOOP_MS;
  return { start, end };
}

/** Clamp helper. */
function clamp01(n) {
  return n < 0 ? 0 : n > 1 ? 1 : n;
}

/**
 * Position of seat `seat`'s representative card at time t. During the deal
 * the card eases from CENTER (the deck) to its resting fan slot; otherwise
 * it sits at rest. The win beat fades cards out (handled via `alpha`),
 * leaving position at rest so the wrap shows no jump.
 */
function cardForSeat(seat, t) {
  const rest = SEAT_REST[seat];
  const { start, end } = windowOf('deal');
  const dealLen = end - start;
  const slot = DEAL_ORDER.indexOf(seat);
  const seatStart = start + slot * DEAL_STAGGER_FRAC * dealLen;
  const seatLen = dealLen * (1 - DEAL_STAGGER_FRAC); // overlap windows
  let dealT = clamp01((t - seatStart) / seatLen);
  // After the deal window every card is fully at rest.
  if (t >= end) dealT = 1;

  const e = easeOut(dealT);
  const x = lerp(CENTER.x, rest.x, e);
  const y = lerp(CENTER.y, rest.y, e);

  // Win-beat fade: ramp alpha 1 → 0 over the back half of the win phase so
  // the table is empty at LOOP_MS and the next deal starts clean.
  const win = windowOf('win');
  let alpha = 1;
  if (t >= win.start) {
    const winT = clamp01((t - win.start) / (win.end - win.start));
    // Hold full for the first 40%, then fade out, gives the win flourish
    // room to land before clearing.
    alpha = 1 - clamp01((winT - 0.4) / 0.6);
  }

  return {
    seat,
    cardId: seat === 1 ? 'JH' : '',       // only the player's card is face-up
    faceUp: seat === 1,
    x: q(x),
    y: q(y),
    alpha: q(alpha),
  };
}

/**
 * Cards currently landed in the center trick area at time t, with each
 * card's fly-in progress. Empty outside the trick phase.
 */
function trickCardsAt(t) {
  const { start, end } = windowOf('trick');
  if (t < start || t >= end) return [];
  const span = end - start;
  // Four plays land evenly across the first 70% of the trick window; the
  // last 30% is a brief winner-pulse hold before the win beat.
  const playWindow = span * 0.7;
  const perPlay = playWindow / TRICK_PLAYS.length;
  const out = [];
  for (let i = 0; i < TRICK_PLAYS.length; i++) {
    const playStart = start + i * perPlay;
    if (t < playStart) continue;
    const progress = clamp01((t - playStart) / perPlay);
    const play = TRICK_PLAYS[i];
    const from = SEAT_REST[play.seat];
    const e = easeOut(progress);
    // Small per-seat fan offset so the 4 cards don't stack exactly.
    const slot = { 1: { dx: 0, dy: 0.06 }, 2: { dx: -0.06, dy: 0 }, 3: { dx: 0, dy: -0.06 }, 4: { dx: 0.06, dy: 0 } }[play.seat];
    const tx = CENTER.x + slot.dx;
    const ty = CENTER.y + slot.dy;
    out.push({
      seat: play.seat,
      cardId: play.cardId,
      x: q(lerp(from.x, tx, e)),
      y: q(lerp(from.y, ty, e)),
      progress: q(progress),
      winner: play.seat === TRICK_WINNER_SEAT,
    });
  }
  return out;
}

/**
 * Win-flourish scale for the winning card during the win beat (spring
 * overshoot), 1 outside the win phase.
 */
function winPulseAt(t) {
  const { start, end } = windowOf('win');
  if (t < start || t >= end) return 1;
  // Pulse over the first 40% of the win window, then settle.
  const winT = clamp01((t - start) / ((end - start) * 0.4));
  return q(1 + (springOvershoot(winT) - 1));
}

// ── public API ────────────────────────────────────────────────────────

/**
 * Describe the hero frame at time `tMs`. Loops with period LOOP_MS:
 * heroFrameAt(t) deep-equals heroFrameAt(t + LOOP_MS).
 *
 * @param {number} tMs  elapsed milliseconds (any value; wrapped into the loop)
 * @returns {{
 *   phase: 'deal'|'order_up'|'trick'|'win',
 *   dealProgress: number,
 *   trumpSuit: 'S'|'H'|'D'|'C'|null,
 *   cards: Array<{seat:number,cardId:string,faceUp:boolean,x:number,y:number,alpha:number}>,
 *   trickCards: Array<{seat:number,cardId:string,x:number,y:number,progress:number,winner:boolean}>,
 *   win: boolean,
 *   winPulse: number,
 * }}
 */
export function heroFrameAt(tMs) {
  // Wrap into [0, LOOP_MS). Works for negative t too.
  const t = ((tMs % LOOP_MS) + LOOP_MS) % LOOP_MS;
  const phase = /** @type {'deal'|'order_up'|'trick'|'win'} */ (phaseAt(t).phase);

  // Deal progress: 0 at deal start, 1 once the last seat has finished.
  const deal = windowOf('deal');
  const dealProgress = q(clamp01((t - deal.start) / (deal.end - deal.start)));

  // Trump is undecided during the deal; ordered up at the order_up beat.
  const trumpSuit = t >= windowOf('order_up').start ? TRUMP_SUIT : null;

  const cards = [1, 2, 3, 4].map((seat) => cardForSeat(seat, t));
  const trickCards = trickCardsAt(t);
  const win = t >= windowOf('win').start;

  return {
    phase,
    dealProgress,
    trumpSuit,
    cards,
    trickCards,
    win,
    winPulse: winPulseAt(t),
  };
}

/**
 * Reduced-motion off-switch. When the user prefers reduced motion we do
 * NOT animate, the component renders heroStaticFrame() once instead.
 *
 * @param {boolean} prefersReducedMotion
 * @returns {boolean}  true → run the rAF loop; false → static frame only
 */
export function shouldAnimateHero(prefersReducedMotion) {
  return !prefersReducedMotion;
}

/**
 * A tasteful single static frame for the reduced-motion path: a fully
 * dealt table mid-hand with trump decided and one card resting in the
 * center. Pure, no time argument.
 *
 * @returns {ReturnType<typeof heroFrameAt>}
 */
export function heroStaticFrame() {
  // Sample a calm mid-hand instant (early in the order_up beat): every
  // card dealt + at rest, trump decided, nothing mid-flight.
  const frame = heroFrameAt(windowOf('order_up').start + 200);
  // Force the canonical "settled" values so the static still never depends
  // on easing residue.
  frame.dealProgress = 1;
  frame.win = false;
  frame.winPulse = 1;
  frame.trickCards = [];
  return frame;
}

export const _internals = {
  PHASE_SCHEDULE,
  TRUMP_SUIT,
  TRICK_PLAYS,
  TRICK_WINNER_SEAT,
  SEAT_REST,
  CENTER,
  phaseAt,
  windowOf,
  cardForSeat,
  trickCardsAt,
};
